import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { StateService, TransitionService, UrlService } from '@uirouter/angular';
import isArray from 'lodash-es/isArray';
import isFunction from 'lodash-es/isFunction';
import isString from 'lodash-es/isString';

import browserHelper from '@utils/browser-helper';
import { environment } from '@environments/environment';
import { IFBWindow } from '@app/models/window-namespace';
import { TranslateService } from '@app/shared/modules/translate/services/translate.service';
import { logger } from '@utils/logger.service';
import { UserManager } from '@app/shared/services/user-manager.service';
import { featureFlags } from '@utils/feature-flags';
import { storage } from '@utils/storage.service';
import { error } from '@utils/app-logs';
import { LanguagesService } from '@app/shared/services/languages.service';

let deregisterTransitionHooks: Array<Function> = [];

@Injectable()
export class AppRunService {
  constructor(
    @Inject('APP') public APP: string,
    @Inject(DOCUMENT) private document: Document,
    @Inject('Window') private window: IFBWindow,
    private languagesService: LanguagesService,
    private stateService: StateService,
    private transitions: TransitionService,
    private translateService: TranslateService,
    private urlService: UrlService,
    private userManager: UserManager
  ) {}

  onInit() {
    if (deregisterTransitionHooks.length) {
      deregisterTransitionHooks.forEach(deregister => deregister());
      deregisterTransitionHooks = [];
    }

    // UI Router errors handler
    this.stateService.defaultErrorHandler(exception => {
      const exceptionsToIgnore = [
        'The transition has been superseded by a different transition',
        'The transition errored'
      ];

      if (exceptionsToIgnore.indexOf(exception.message) !== -1) {
        return;
      }

      if (environment.DEBUG) {
        logger.log(exception);
      } else {
        // Log error to datadog
        error('UiRouter defaultErrorHandler', exception);
      }
    });

    const isInjectableFunction = value => {
      if (isFunction(value)) {
        return true;
      }

      if (isArray(value) && isFunction(value[value.length - 1])) {
        return true;
      }

      return false;
    };

    const wrapBodyClassInArray = value => {
      if (isString(value)) {
        return [value];
      }
      return value;
    };

    const getBodyClasses = (value: string | string[] | Function): string[] => {
      return isInjectableFunction(value) ? (value as Function)(this.APP) : wrapBodyClassInArray(value);
    };

    deregisterTransitionHooks.push(
      this.transitions.onError({}, transition => {
        const error = transition.error();

        const errorMessage = error?.detail?.message || error?.message || error?.toString() || '';
        const isChunkError =
          /Loading chunk [\d]+ failed/i.test(errorMessage) ||
          /ChunkLoadError/i.test(errorMessage) ||
          /Failed to (fetch|load)/i.test(errorMessage) ||
          /Failed to fetch dynamically imported module/i.test(errorMessage) ||
          /\.(module|chunk)-[A-Z0-9]+\.js/i.test(errorMessage) ||
          (/404|ERR_ABORTED/i.test(errorMessage) && /\/chunk-[A-Z0-9]+\.js/i.test(errorMessage));

        if (isChunkError) {
          logger.warn('ChunkLoadError detected during route transition. Reloading page.', errorMessage, error);
          const chunkReloadAttemptedKey = 'chunk-reload-attempted';

          const hasAttemptedReload = sessionStorage.getItem(chunkReloadAttemptedKey);
          if (!hasAttemptedReload) {
            sessionStorage.setItem(chunkReloadAttemptedKey, 'true');
            window.location.reload();
          } else {
            sessionStorage.removeItem(chunkReloadAttemptedKey);
            logger.error('ChunkLoadError persisted after reload attempt:', error);
          }
        } else {
          logger.error('transition error: ', transition.targetState().name(), error);
        }
      })
    );

    deregisterTransitionHooks.push(
      this.transitions.onBefore(
        {
          to: state => Boolean(state.data?.feature)
        },
        transition => {
          const to = transition.to();
          const featureEnabled = featureFlags.isOn(to.data.feature);
          const featureHide = Boolean(to.data.featureHide);

          if (featureEnabled === featureHide) {
            logger.warn(
              'Navigation to state %s prevented as feature %s is %s',
              to.name,
              to.data.feature,
              featureEnabled ? 'enabled' : 'disabled'
            );

            return false;
          }
        }
      )
    );

    deregisterTransitionHooks.push(
      this.transitions.onStart({}, transition => {
        // Search tag: restore scroll position
        // Clear restoreScrollPosition from localStorage when user didn't click Back button
        // This solution is the simplest one, but works for 90% of cases
        const restoreScrollPosition = storage.getItem('restoreScrollPosition');
        if (restoreScrollPosition && restoreScrollPosition.stateFrom !== transition.to().name) {
          storage.removeItem('restoreScrollPosition');
        }
      })
    );

    deregisterTransitionHooks.push(
      this.transitions.onSuccess({}, transition => {
        // Short delay ensures state finished initialization, otherwise url changes could be pending
        const to = transition.to();
        const from = transition.from();
        const route = this.urlService.path();

        // Safely instantiate dataLayer
        const dataLayer = (this.window.dataLayer = this.window.dataLayer || []);

        dataLayer.push({
          event: 'RouteChange',
          attributes: { route }
        });

        // Remove class from body from the previous state
        if (from.data?.bodyClass) {
          const bodyClass = getBodyClasses(from.data.bodyClass);
          this.document.body.classList.remove(...bodyClass);
        }

        // Add class to body from the current state
        if (to.data?.bodyClass) {
          // @NB bodyClass can be a function, which is called with APP as a parameter.
          // For now it's the only parameter passed, there is a type to enforce it: ModuleStatesDeclaration
          const bodyClass = getBodyClasses(to.data.bodyClass);
          this.document.body.classList.add(...bodyClass);
        }

        let path;
        if (browserHelper.isInWrapper) {
          // In the mobile app path are stored in location hash, obtain url path data from there
          path = this.window.location.hash.replace('#/', '/');

          if (
            path.indexOf('/login') !== 0 &&
            path.indexOf('/intro') !== 0 &&
            path.indexOf('/scan-qr-code') !== 0 &&
            path.indexOf('/qr-code-scanner') !== 0 &&
            path.indexOf('/enter-domain') !== 0
          ) {
            // There is no /dashboard prefix in mobile app, add it manually
            path = `/dashboard${path}`;
          }
        } else {
          // Path in browser
          path = `${this.window.location.pathname}${this.window.location.search}`;
        }

        // Force scroll page to top on every route change
        const scrollingContainer = this.document.getElementById('scrolling-container');
        if (scrollingContainer) {
          scrollingContainer.scrollTop = 0;
        }
      })
    );
  }

  onBootstrap() {
    this.translateService.setLocale(this.userManager.data.locale || browserHelper.preferredLanguage);
    this.languagesService.checkFeatureToggled();
  }
}
